import { z } from 'zod';
import { idSchema } from './schemas/common.ts';

// Bound stored configuration separately from visible options and ledger history.
export const MAX_CONFIGURED_INVOICE_OPTIONS = 100;
export const MAX_VISIBLE_INVOICE_OPTIONS = 20;

export const INVOICE_OPTION_PROVIDERS = ['venmo', 'cash_app', 'paypal', 'zelle', 'custom'] as const;
export const INVOICE_OPTION_PROVIDER_LABELS = {
  venmo: 'Venmo',
  cash_app: 'Cash App',
  paypal: 'PayPal',
  zelle: 'Zelle',
  custom: 'Custom',
};
const hosts: Record<string, readonly string[]> = {
  venmo: ['venmo.com', 'www.venmo.com', 'account.venmo.com'],
  cash_app: ['cash.app'],
  paypal: ['paypal.me', 'www.paypal.me', 'paypal.com', 'www.paypal.com'],
};
const publicQueryKeys = new Set([
  'amount',
  'currency',
  'currency_code',
  'txn',
  'cmd',
  'hosted_button_id',
  'utm_source',
  'utm_medium',
  'utm_campaign',
]);

/** Never constructs, expands, fetches, or adds invoice/customer data to a destination. */
export function validInvoicePaymentUrl(raw: string, provider: string, customHost = ''): boolean {
  try {
    if (/[\s<>\\\u0000-\u001f]/.test(raw) || raw.length > 1000) return false;
    const url = new URL(raw);
    if (url.protocol !== 'https:' || url.username || url.password || url.port || url.hash) return false;
    if (provider === 'zelle') return false; // Use enrolled recipient instructions / a bank-issued PNG.
    if (provider === 'custom') {
      if (
        url.hostname !== customHost.toLowerCase() ||
        !/^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/.test(customHost)
      )
        return false;
      if (/(^|\.)(localhost|local|internal|test|invalid|example)$/.test(customHost) || customHost.includes('xn--'))
        return false;
      // Branded hosts must still use the matching provider validation.
      if (Object.values(hosts).flat().includes(url.hostname)) return false;
    } else if (!hosts[provider]?.includes(url.hostname)) return false;
    const decoded = decodeURIComponent(url.pathname + url.search);
    if (
      /[<>\u0000-\u001f]/.test(decoded) ||
      /\/(?:i|p|pay)\/[0-9A-Za-z._-]{20,}/.test(decoded) ||
      /(?:sk_|pk_live_|secret|session|password|bearer|access_token|portal_token)/i.test(decoded)
    )
      return false;
    for (const [key, value] of url.searchParams) {
      if (!publicQueryKeys.has(key) || /https?:|[<>{}]/i.test(value)) return false;
    }
    if (provider === 'venmo' && !/^\/(?:u\/|business\/)?[a-zA-Z0-9_-]+\/?$/.test(url.pathname)) return false;
    if (provider === 'cash_app' && !/^\/\$[a-zA-Z0-9_]+(?:\/\d+(?:\.\d{1,2})?)?\/?$/.test(url.pathname)) return false;
    if (
      provider === 'paypal' &&
      url.hostname.endsWith('paypal.me') &&
      !/^\/[a-zA-Z0-9]+(?:\/\d+(?:\.\d{1,2})?(?:[A-Za-z]{3})?)?\/?$/.test(url.pathname)
    )
      return false;
    if (
      provider === 'paypal' &&
      url.hostname.endsWith('paypal.com') &&
      !/^\/(?:ncp\/payment\/[a-zA-Z0-9]+|pay\/[^/]+|cgi-bin\/webscr)\/?$/.test(url.pathname)
    )
      return false;
    return url.pathname !== '/';
  } catch {
    return false;
  }
}

const plain = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .regex(/^[^<>\u0000-\u0008\u000b-\u001f]*$/, 'Use plain text, without HTML.');
// A bank-exported PNG only: no SVG, HTML, or remote image requests.
const bankQr = z
  .string()
  .max(140000)
  .refine(
    (value) => value === '' || /^data:image\/png;base64,iVBORw0KGgo[A-Za-z0-9+/=]+$/.test(value),
    'Use a PNG exported by your bank.',
  );
const retainedInvoicePaymentOptionSchema = z
  .object({
    methodId: idSchema,
    showOnInvoices: z.boolean(),
    provider: z.enum(INVOICE_OPTION_PROVIDERS),
    url: z.string().trim().max(1000),
    customHost: plain(253),
    recipientName: plain(100),
    recipientInstructions: plain(240),
    instructions: plain(240),
    bankQrDataUrl: bankQr,
  })
  .strict()
  .superRefine((option, ctx) => {
    const issue = (path: string, message: string) => ctx.addIssue({ code: 'custom', path: [path], message });
    if (option.provider !== 'custom' && option.customHost)
      issue('customHost', 'Custom hostnames are only for Custom providers.');
    if (option.provider !== 'zelle' && option.recipientInstructions)
      issue('recipientInstructions', 'Recipient instructions are only for Zelle.');
    if (option.provider === 'zelle') {
      if (option.url) issue('url', 'Use your enrolled Zelle recipient instructions, not a generated payment URL.');
    } else {
      if (option.url && !validInvoicePaymentUrl(option.url, option.provider, option.customHost))
        issue(
          'url',
          'Enter an actual HTTPS business link on the provider’s supported host, without private tokens or redirect parameters.',
        );
      if (option.bankQrDataUrl) issue('bankQrDataUrl', 'Bank QR images are only for Zelle.');
    }
  });
function requireShownDestination(
  option: z.infer<typeof retainedInvoicePaymentOptionSchema>,
  issue: (path: string, message: string) => void,
) {
  if (!option.showOnInvoices) return;
  if (!option.recipientName) issue('recipientName', 'Enter the recipient display name.');
  if (option.provider === 'zelle') {
    if (!option.recipientInstructions)
      issue('recipientInstructions', 'Enter the recipient details supplied by your bank.');
  } else if (!option.url) {
    issue(
      'url',
      'Enter an actual HTTPS business link on the provider’s supported host, without private tokens or redirect parameters.',
    );
  }
}
// Public rendering still requires a complete destination. Hidden/inherited
// drafts retain safe incomplete values without making them customer-visible.
export const invoicePaymentOptionSchema = retainedInvoicePaymentOptionSchema.superRefine((option, ctx) => {
  requireShownDestination(option, (path, message) => ctx.addIssue({ code: 'custom', path: [path], message }));
});
export const invoiceOptionsSchema = z
  .object({
    mode: z.enum(['inherit', 'override', 'hide']),
    options: z.array(retainedInvoicePaymentOptionSchema).max(MAX_CONFIGURED_INVOICE_OPTIONS),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.mode === 'override')
      value.options.forEach((option, index) => {
        requireShownDestination(option, (path, message) =>
          ctx.addIssue({ code: 'custom', path: ['options', index, path], message }),
        );
      });
    if (new Set(value.options.map((option) => option.methodId)).size !== value.options.length)
      ctx.addIssue({ code: 'custom', path: ['options'], message: 'A method can only appear once.' });
  });
export const saveInvoiceOptionsSchema = invoiceOptionsSchema.safeExtend({ version: z.number().int().nonnegative() });
export type InvoicePaymentOption = z.infer<typeof invoicePaymentOptionSchema>;
export type InvoiceOptionsConfig = z.infer<typeof invoiceOptionsSchema>;
export type InvoiceOptionsResponse = InvoiceOptionsConfig & {
  version: number;
  locationId: string | null;
  companyId: string;
  effectiveOptions: InvoicePaymentOption[];
  companyOptions: InvoicePaymentOption[];
  inheritingLocations: string[];
};
export const emptyInvoicePaymentOption = (methodId: string): InvoicePaymentOption => ({
  methodId,
  showOnInvoices: false,
  provider: 'custom',
  url: '',
  customHost: '',
  recipientName: '',
  recipientInstructions: '',
  instructions: '',
  bankQrDataUrl: '',
});

/** Omit only untouched defaults; retain every configured value even when off/inactive. */
export function configuredInvoicePaymentOptions(options: readonly InvoicePaymentOption[]): InvoicePaymentOption[] {
  return options.filter(
    (option) =>
      option.showOnInvoices ||
      option.provider !== 'custom' ||
      [
        option.url,
        option.customHost,
        option.recipientName,
        option.recipientInstructions,
        option.instructions,
        option.bankQrDataUrl,
      ].some(Boolean),
  );
}

export function resolveInvoiceOptions(
  company: InvoiceOptionsConfig,
  location: InvoiceOptionsConfig | null,
  blocked = false,
): InvoicePaymentOption[] {
  if (blocked || location?.mode === 'hide') return [];
  return (location?.mode === 'override' ? location.options : company.options).filter((option) => option.showOnInvoices);
}
